import type * as Types from '@aeppic/types'
import type { SingleQueryStats } from 'model/indexing/query-stats.js'
import type {
  DocumentMatchIterator,
  DocumentMatchIteratorJoined,
  DocumentMatchIteratorUsingId,
  DocumentMatchIteratorUsingIndex,
  KMergedIntersectionIterator,
  KMergedUnionIterator
} from './iterator-types.js'
import type { EnumerationStrategy } from './enumeration/strategy.js'
import { legacyEnumerationStrategy } from './enumeration/legacy.js'
import { experimentalEnumerationStrategy } from './enumeration/experimental.js'

export * from './iterator-types.js'
export * from './iterator-builders.js'
export * from './enumeration/strategy.js'

/**
 * Toggle this flag to switch between the legacy and experimental enumeration flows.
 * The value is intentionally hardcoded so we can flip implementations without
 * exposing additional runtime configuration.
 */
const USE_EXPERIMENTAL_ENUMERATION = true

let activeEnumerationStrategy: EnumerationStrategy = USE_EXPERIMENTAL_ENUMERATION
  ? experimentalEnumerationStrategy
  : legacyEnumerationStrategy

export function setEnumerationStrategy(strategy: EnumerationStrategy): void {
  activeEnumerationStrategy = strategy
}

export function getEnumerationStrategy(): EnumerationStrategy {
  return activeEnumerationStrategy
}

export function resetEnumerationStrategy(): void {
  activeEnumerationStrategy = legacyEnumerationStrategy
}

export function useExperimentalEnumerationStrategy(): void {
  activeEnumerationStrategy = experimentalEnumerationStrategy
}

export function useLegacyEnumerationStrategy(): void {
  activeEnumerationStrategy = legacyEnumerationStrategy
}

export function getDocumentIdGenerator(
  iterator: DocumentMatchIterator,
  allDocuments?: Map<string, Types.Document>
): Generator<Types.DocumentId, void, unknown> {
  return activeEnumerationStrategy.getDocumentIdGenerator(iterator, allDocuments)
}

export function enumerateKMergedUnion(
  iterator: KMergedUnionIterator,
  allDocuments?: Map<string, Types.Document>,
  stats?: SingleQueryStats
): Generator<Types.DocumentId, void, unknown> {
  return activeEnumerationStrategy.enumerateKMergedUnion(iterator, allDocuments, stats)
}

export function enumerateKMergedIntersection(
  iterator: KMergedIntersectionIterator,
  allDocuments?: Map<string, Types.Document>,
  stats?: SingleQueryStats
): Generator<Types.DocumentId, void, unknown> {
  return activeEnumerationStrategy.enumerateKMergedIntersection(iterator, allDocuments, stats)
}

export function enumerateAllDocumentsInIndexIterator(
  iterator: DocumentMatchIteratorUsingIndex
): IterableIterator<Types.DocumentId> {
  return activeEnumerationStrategy.enumerateAllDocumentsInIndexIterator(iterator)
}

export function iteratorContains(
  iterator: DocumentMatchIteratorUsingIndex,
  documentId: Types.DocumentId
): boolean {
  return activeEnumerationStrategy.iteratorContains(iterator, documentId)
}

export function enumerateGraphIterator(
  iterator: DocumentMatchIterator,
  allDocuments: Map<string, Types.Document>,
  stats?: SingleQueryStats
): IterableIterator<Types.Document> {
  return activeEnumerationStrategy.enumerateGraphIterator(iterator, allDocuments, stats)
}

export function enumerateIntersectionOfIterators(
  iterators: DocumentMatchIterator[],
  allDocuments: Map<string, Types.Document>,
  stats?: SingleQueryStats
): IterableIterator<Types.Document> {
  return activeEnumerationStrategy.enumerateIntersectionOfIterators(iterators, allDocuments, stats)
}

export function enumerateUnionOfIterators(
  iterators: (DocumentMatchIteratorJoined | DocumentMatchIteratorUsingIndex | DocumentMatchIteratorUsingId)[],
  allDocuments: Map<string, Types.Document>,
  stats?: SingleQueryStats
): IterableIterator<Types.Document> {
  return activeEnumerationStrategy.enumerateUnionOfIterators(iterators, allDocuments, stats)
}

export function _enumerateBTreeIntersection(
  iterators: DocumentMatchIteratorUsingIndex[],
  allDocuments: Map<string, Types.Document>,
  stats?: SingleQueryStats
): IterableIterator<Types.Document> {
  return activeEnumerationStrategy._enumerateBTreeIntersection(iterators, allDocuments, stats)
}
